const consentAttributes = ['data-cookieblock-src', 'data-cookieconsent', 'data-usercentrics', 'data-src'];

export const youtubePreviewVideoId = (iframe) => {
    // CMPs own these frames, including their placeholders and consent revocation.
    if (consentAttributes.some((name) => iframe.hasAttribute(name))) {
        return null;
    }

    try {
        const url = new URL(iframe.getAttribute('src'), document.baseURI);
        if (!['https:', 'http:'].includes(url.protocol)
            || !['youtube.com', 'www.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(url.hostname)
            || url.pathname === '/embed/videoseries'
            || url.searchParams.get('autoplay') === '1') {
            return null;
        }
        return url.pathname.match(/^\/embed\/([a-zA-Z0-9_-]{11})\/?$/)?.[1] || null;
    } catch (_error) {
        return null;
    }
};

export const loadYoutubePreview = (videoId, ImageClass = Image) => new Promise((resolve) => {
    const candidates = ['maxresdefault', 'sddefault', 'hqdefault'];
    const image = new ImageClass();
    image.alt = '';
    image.decoding = 'async';
    let index = 0;
    const next = () => {
        if (index === candidates.length) {
            image.onload = image.onerror = null;
            resolve(null);
            return;
        }
        image.src = `https://i.ytimg.com/vi/${videoId}/${candidates[index++]}.jpg`;
    };
    image.onload = () => {
        // YouTube can return a 120px placeholder with HTTP 200 for missing sizes.
        if (image.naturalWidth <= 120) {
            next();
            return;
        }
        image.onload = image.onerror = null;
        resolve(image);
    };
    image.onerror = next;
    next();
});

export const enhanceYoutubePreviews = (root, processed) => {
    root.querySelectorAll('iframe[src]').forEach(async (iframe) => {
        const videoId = youtubePreviewVideoId(iframe);
        if (!videoId || processed.has(iframe)) {
            return;
        }
        processed.add(iframe);
        const src = iframe.getAttribute('src');
        const preview = document.createElement('button');
        preview.type = 'button';
        preview.className = 'kh-youtube-preview';
        const label = window.khThemeRuntime?.youtube?.play || 'Play video';
        preview.setAttribute('aria-label', iframe.title ? `${label}: ${iframe.title}` : label);
        preview.style.width = iframe.style.width || (/^\d+$/.test(iframe.getAttribute('width') || '')
            ? `${iframe.getAttribute('width')}px` : '100%');
        preview.style.height = iframe.style.height;
        const width = Number(iframe.getAttribute('width'));
        const height = Number(iframe.getAttribute('height'));
        preview.style.aspectRatio = width > 0 && height > 0 ? `${width} / ${height}` : '16 / 9';
        const icon = document.createElement('span');
        icon.className = 'kh-youtube-preview__play';
        icon.setAttribute('aria-hidden', 'true');
        preview.append(icon);

        preview.addEventListener('click', () => {
            const url = new URL(src, document.baseURI);
            url.searchParams.set('autoplay', '1');
            const allow = iframe.getAttribute('allow') || '';
            if (!allow.split(';').some((permission) => permission.trim().split(/\s/)[0] === 'autoplay')) {
                iframe.setAttribute('allow', `${allow}${allow ? '; ' : ''}autoplay`);
            }
            iframe.setAttribute('src', url.href);
            preview.replaceWith(iframe);
            iframe.focus();
        }, {once: true});

        // Keep the original node and all player attributes for playback.
        iframe.replaceWith(preview);
        iframe.removeAttribute('src');

        const image = await loadYoutubePreview(videoId);
        if (!preview.isConnected) {
            return;
        }
        if (image) {
            preview.prepend(image);
        } else {
            // A network error must never leave a broken player placeholder.
            iframe.setAttribute('src', src);
            preview.replaceWith(iframe);
        }
    });
};

export const bootYoutubePreviews = () => {
    const processed = new WeakSet();
    const refresh = () => enhanceYoutubePreviews(document, processed);
    refresh();
    if (!('MutationObserver' in window)) {
        return;
    }
    let scheduled = false;
    const observer = new MutationObserver(() => {
        if (scheduled) {
            return;
        }
        scheduled = true;
        window.requestAnimationFrame(() => {
            scheduled = false;
            refresh();
        });
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src'],
    });
};
